// Mirrors Finkit.Tenants.User.TraderSurveillance contracts
// (SuspiciousTrades + AlertsProfile app services). Wire enums are numeric;
// do not renumber. JSON is camelCased.

// PaginatedInputDto (Finkit.Needs.Pagination) — the server-side paging/sort contract.
export interface PaginatedInputDto {
  skipCount?: number;
  maxResultCount?: number;
  sorting?: string;
  draw?: number;
  search?: string;
}

// ---- shared enums (Finkit.Needs / Finkit.Enums) ----
export enum TsAlertProfileType {
  AccountConditions = 0,
  ClosedTrades = 1,
  Deals = 2,
}

export enum TsTradeSession {
  NotApplicable = 0,
  Sydney = 1,
  Tokyo = 2,
  London = 3,
  NewYork = 4,
  Custom = 5,
}

export enum TsComparisonOperator {
  NotApplicable = 0,
  GreaterThan = 1,
  SmallerThan = 2,
  EqualTo = 3,
}

export enum MiFIDClassification {
  Retail = 0,
  PerSeProfessional = 1,
  ProfessionalOnRequest = 2,
  EligibleCounterparty = 3,
  Default = 4,
}

// TsReason — NotApplicable (-1) is excluded from the filter dropdown.
export enum TsReason {
  NotApplicable = -1,
  Client = 0,
  Expert = 1,
  Dealer = 2,
  Signal = 10,
  Mobile = 16,
  Web = 17,
}

export enum TsAlertStatus {
  Pending = 0,
  Resolved = 1,
}

export interface SelectListItem {
  value: string;
  text: string;
}

// ---- Suspicious Trades DTOs ----
export interface TraderSurveillanceAlertDto {
  id: number;
  profileId: string;
  profileTag?: string | null;
  priority?: string | null;
  uniqueId?: string | null;
  login: number;
  ticket: number;
  susDate: string;
  status: TsAlertStatus;
  statusName?: string | null;
  resolveDate?: string | null;
  commentDate?: string | null;
  susTypes?: string | null;
  susTypesDisplay?: string | null;
  susComment?: string | null;
  profit: number;
  profitRc: number;
  floatingAc: number;
  tsComment?: string | null;
  connectorId: number;
  connector?: string | null;
  openTickets?: string | null;
  group?: string | null;
  mifidClassification?: MiFIDClassification | null;
  country?: string | null;
  byUniqueId: boolean;
  limit?: number | null;
}

export interface TsAlertInfoDto {
  id: number;
  byUniqueId: boolean;
  login: number;
  connector?: string | null;
  uniqueId?: string | null;
  group?: string | null;
  country?: string | null;
  mifidClassification?: MiFIDClassification | null;
  mifidClassificationName?: string | null;
  date?: string | null;
  ticket: number;
  profileTag?: string | null;
  susTypesDisplay?: string | null;
  userAsAlertType: boolean;
  value?: number | null;
  valueRc?: number | null;
  marginLevel?: number | null;
  profitRc?: number | null;
  isProfitThreshold: boolean;
  isCumulativeProfit: boolean;
  tsComment?: string | null;
  resolved: number;
  dealAsTradeType: boolean;
  tradeAsTradeType: boolean;
}

export interface TsAlertTradeRowDto {
  ticket: number;
  name?: string | null;
  uniqueId?: string | null;
  login: number;
  currency?: string | null;
  type?: string | null;
  volume: number;
  instrument?: string | null;
  openPrice: number;
  closePrice?: number | null;
  openTime: string;
  closeTime?: string | null;
  profit: number;
  profitRc: number;
  notional: number;
  notionalRc: number;
  connector?: string | null;
  connectorId: number;
  isNotional: boolean;
  isDeal: boolean;
}

export interface TsAuditTrailRowDto {
  date: string;
  user?: string | null;
  action?: string | null;
  newComment?: string | null;
  oldComment?: string | null;
}

export interface TsAlertCountsDto {
  alertsArisenCount: number;
  alertsPendingCount: number;
  alertsResolvedCount: number;
  relatedUniqueIdsCount: number;
}

// TsAlertFilterDto : PaginatedInputDto. Only non-empty fields are sent.
export interface TsAlertFilterDto extends PaginatedInputDto {
  id?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  status?: number | null;
  uniqueId?: string | null;
  login?: number | null;
  priority?: string[];
  connector?: number[];
  country?: string[];
  profileTagId?: string[];
}

export interface TsResolveAllInput extends TsAlertFilterDto {
  comment: string;
}

export interface TsAlertOperationInput {
  alertId: number;
  tsComment?: string | null;
}

// ---- Alerts Profile DTOs ----
export interface TsProfileFilterDto extends PaginatedInputDto {
  profileTag?: string;
}

export interface CreateTraderSurveillanceProfileInput {
  profileTag: string;
  priority?: string | null;
  historyDate: string; // YYYY-MM-DD
  type?: TsAlertProfileType | null;
  tradeSession?: TsTradeSession | null;
  tradeTime?: number | null;
  tradePoints?: number | null;
  cumulativeProfitThresholds: boolean;
  cumulativeProfitThresholdsAmount?: number | null;
  cumulativeProfitThresholdsFloating: boolean;
  cumulativeProfitThresholdsPeriod?: string | null;
  profitThresholdsPerTrade: boolean;
  profitThresholdsAmountPerTrade?: number | null;
  volume?: number | null;
  volumeOperator?: TsComparisonOperator | null;
  notional?: number | null;
  notionalOperator?: TsComparisonOperator | null;
  openTime?: string | null; // YYYY-MM-DDTHH:mm:ss
  closeTime?: string | null;
  equity?: number | null;
  equityOperator?: TsComparisonOperator | null;
  marginLevel?: number | null;
  marginLevelOperator?: TsComparisonOperator | null;
  overnightDays?: number | null;
  reason?: number | null;
  reportingCurrency: boolean;
  byUniqueId: boolean;
  instrument?: string | null;
  marInstruments: boolean;
  uniqueId?: string | null;
  login?: number | null;
  connector?: number | null;
  mifidClassification?: MiFIDClassification | null;
  group?: string | null;
  accountCurrency?: string | null;
  countryId?: number | null;
}

export interface TraderSurveillanceProfileDto extends CreateTraderSurveillanceProfileInput {
  id: string;
  typeName?: string | null;
  enabled: boolean;
  countryName?: string | null;
  creationTime: string;
}
